import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';

const schema = z
  .object({
    currentStep: z.number().default(0).optional(),
    app: z.string().nonempty('Application is required'),
    platform: z.string().optional(),
    marketingProcess: z.enum(['inbox', 'testing', 'scaling'], {
      errorMap: () => ({ message: 'Marketing process is required' }),
    }),
    adType: z.string().optional(),
    adAccounts: z.array(z.string()).optional(),
    selectedCampaigns: z.array(z.any()).optional(),
    selectedCampaignsOnly: z.boolean(),
    selectedAdSets: z.array(z.any()).optional(),
    selectedCampaignsBudgets: z.record(
      z.string(),
      z.string()
        .refine((val) => val.trim() !== '', {
          message: 'Cannot be empty',
        }),
    ),
    selectedAdSetsBudgets: z.record(
      z.string(),
      z.string()
        .refine((val) => val.trim() !== '', {
          message: 'Cannot be empty',
        }),
    ),
    selectedAdSetsDuplicatesCount: z.record(
      z.string(),
      z.string()
        .refine((val) => val.trim() !== '', {
          message: 'Cannot be empty',
        })
        .refine((val) => {
          const num = Number(val);
          return !isNaN(num) && num <= 5;
        }, {
          message: 'Cannot be more than 5',
        })),
    selectedAdSetsStartTimes: z.record(z.string(), z.string()).default({}),
    selectedCreatives: z.array(z.string()).default([]),
    callToActions: z.string().optional(),
    selectedConversionDomain: z.string().optional(),
    selectedAdSettings: z.record(z.unknown()).optional(),
    hotjar: z.boolean().optional(),
    conversionDomain: z.string().optional(),
    adSettings: z.string().optional(),
    adLabelSuffix: z.string().optional(),
    packsWithCreatives: z
      .array(
        z.object({
          tag: z.string().default(''),
          creatives: z.array(z.string()),
          filterCreativeByName: z.array(z.string()).default([]),
          visual_touch_ups: z.boolean().default(true),
        }),
      )
      .default([{ tag: '', creatives: [], visual_touch_ups: true }]),
    activePacksWithCreatives: z.number().nullable().optional(),
    activePackTag: z.string().default(''),
    filterCampaignsByName: z.array(z.string()).default([]),
    filterAdSetsByName: z.array(z.string()).default([]),
  })
  .superRefine((data, ctx) => {
    const currentStep = data.currentStep;

    if (currentStep === 0 && !data?.app) {
      ctx.addIssue({
        code: 'custom',
        message: 'Application is required',
        path: ['app'],
      });
    }

    if (currentStep === 0 && !data?.marketingProcess) {
      ctx.addIssue({
        code: 'custom',
        message: 'Marketing process is required',
        path: ['marketingProcess'],
      });
    }

    if (currentStep === 1 && !data?.adAccounts?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'AdAccounts is required at this step',
        path: ['adAccounts'],
      });
    }

    if (currentStep === 2 && !data?.selectedCampaigns?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign must be selected at this step',
        path: ['selectedCampaigns'],
      });
    }

    if (currentStep === 3 && !data?.selectedAdSets?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one ad set must be selected at this step',
        path: ['selectedAdSets'],
      });
    }

    if (
      currentStep === 4 &&
      !data?.selectedCreatives?.length &&
      data.marketingProcess === 'inbox'
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one creative has to be applied',
        path: ['selectedCreatives'],
      });
    }

    if (
      currentStep === 4 &&
      ['testing', 'scaling'].includes(data.marketingProcess)
    ) {
      const isSomePackEmpty = data.packsWithCreatives.findIndex(
        (pack) => !pack.creatives.length,
      );

      if (isSomePackEmpty !== -1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select creatives to continue',
          path: [`packsWithCreatives.${isSomePackEmpty}`, 'creatives'],
        });
      }
    }

    if (currentStep === 5 && !data?.callToActions) {
      ctx.addIssue({
        code: 'custom',
        message: 'Can\'t be empty',
        path: ['callToActions'],
      });
    }

    if (
      currentStep === 5 &&
      data.platform === 'WEB' &&
      !data?.selectedConversionDomain?.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Can\'t be empty',
        path: ['selectedConversionDomain'],
      });
    }
  });

export type FacebookFormData = z.infer<typeof schema>;

export default {
  schema,
  resolver: zodResolver(schema),
} as {
  schema: typeof schema;
  resolver: Resolver<z.infer<typeof schema>>;
};
